import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Send, Loader2 } from "lucide-react";
import { SERVICES } from "../data/services";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = ({ t, lang }) => {
  const [form, setForm] = useState({ name: "", email: "", phone: "", service: "", message: "" });
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/inquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, lang }),
      });
      if (!res.ok) throw new Error();
      toast.success(t.contact.success);
      setForm({ name: "", email: "", phone: "", service: "", message: "" });
    } catch {
      toast.error(t.contact.error);
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full rounded-xl border border-white/10 bg-[#0C1018] px-4 py-3 text-sm text-white placeholder:text-slate-500 outline-none transition-colors duration-300 focus:border-amber-400/60";

  return (
    <section id="contact" data-testid="contact-section" className="scroll-mt-24 px-5 py-24 sm:px-8 sm:py-32">
      <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-2 lg:gap-20">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
        >
          <span className="text-xs font-semibold uppercase tracking-[0.28em] text-amber-400">{t.contact.eyebrow}</span>
          <h2 className="font-display mt-4 text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl">{t.contact.title}</h2>
          <p className="mt-4 max-w-md text-base text-slate-400 sm:text-lg">{t.contact.sub}</p>
          <div className="gold-trace mt-10 rounded-2xl border border-amber-400/20 bg-amber-400/[0.04] p-6">
            <p className="font-display text-lg italic text-amber-200">"Sagas Logistic"</p>
            <p className="mt-2 text-sm text-slate-400">Caracas · Croton-on-Hudson, NY</p>
            <a data-testid="contact-email-link" href="mailto:Sagaslogistic@gmail.com" className="mt-1 block text-sm text-amber-300 transition-colors hover:text-amber-200">Sagaslogistic@gmail.com</a>
          </div>
        </motion.div>

        <motion.form
          data-testid="consultation-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="rounded-2xl border border-white/10 bg-[#121722]/80 p-6 backdrop-blur-xl sm:p-8"
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="cf-name" className="mb-2 block text-xs font-semibold uppercase tracking-widest text-slate-400">{t.contact.name}</label>
              <input id="cf-name" data-testid="consultation-form-name-input" required value={form.name} onChange={set("name")} placeholder={t.contact.namePh} className={inputCls} />
            </div>
            <div>
              <label htmlFor="cf-email" className="mb-2 block text-xs font-semibold uppercase tracking-widest text-slate-400">{t.contact.email}</label>
              <input id="cf-email" data-testid="consultation-form-email-input" required type="email" value={form.email} onChange={set("email")} placeholder={t.contact.emailPh} className={inputCls} />
            </div>
            <div>
              <label htmlFor="cf-phone" className="mb-2 block text-xs font-semibold uppercase tracking-widest text-slate-400">{t.contact.phone}</label>
              <input id="cf-phone" data-testid="consultation-form-phone-input" value={form.phone} onChange={set("phone")} placeholder={t.contact.phonePh} className={inputCls} />
            </div>
            <div>
              <label htmlFor="cf-service" className="mb-2 block text-xs font-semibold uppercase tracking-widest text-slate-400">{t.contact.service}</label>
              <select id="cf-service" data-testid="consultation-form-service-select" value={form.service} onChange={set("service")} className={inputCls}>
                <option value="">{t.contact.other}</option>
                {SERVICES.map((s) => (
                  <option key={s.id} value={s.title[lang]}>{s.title[lang]}</option>
                ))}
              </select>
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="cf-message" className="mb-2 block text-xs font-semibold uppercase tracking-widest text-slate-400">{t.contact.message}</label>
              <textarea id="cf-message" data-testid="consultation-form-message-textarea" required rows={4} value={form.message} onChange={set("message")} placeholder={t.contact.messagePh} className={`${inputCls} resize-none`} />
            </div>
          </div>
          <button
            data-testid="consultation-form-submit"
            type="submit"
            disabled={sending}
            className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-full bg-amber-400 px-7 py-3.5 text-sm font-bold text-slate-950 transition-colors duration-300 hover:bg-amber-300 disabled:opacity-60"
          >
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {sending ? t.contact.sending : t.contact.submit}
          </button>
        </motion.form>
      </div>
    </section>
  );
};
